import { useCallback, useMemo, useState } from "react";

// Shared table state for the wallboard's searchable/sortable/paginated tables
// (feedback #6). Produces the URLSearchParams the server endpoints expect, and a
// `toggleSort` that flips asc/desc on the active column. pageSize is fixed; the
// total comes from the server so the UI can always show "X–Y of Z".
export const PAGE_SIZE = 50;

export interface TableQuery {
  search: string;
  setSearch: (v: string) => void;
  sort: string;
  order: "asc" | "desc";
  toggleSort: (field: string) => void;
  page: number;
  setPage: (p: number) => void;
  pageSize: number;
  params: URLSearchParams;
}

export function useTableQuery(
  defaultSort: string,
  defaultOrder: "asc" | "desc" = "desc",
): TableQuery {
  const [search, setSearchRaw] = useState("");
  const [sort, setSort] = useState(defaultSort);
  const [order, setOrder] = useState<"asc" | "desc">(defaultOrder);
  const [page, setPage] = useState(1);

  const setSearch = useCallback((v: string) => {
    setSearchRaw(v);
    setPage(1);
  }, []);

  const toggleSort = useCallback((field: string) => {
    setPage(1);
    setSort((cur) => {
      if (cur === field) {
        setOrder((o) => (o === "asc" ? "desc" : "asc"));
        return cur;
      }
      setOrder("desc");
      return field;
    });
  }, []);

  const params = useMemo(() => {
    const p = new URLSearchParams({
      sort,
      order,
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });
    if (search) p.set("search", search);
    return p;
  }, [search, sort, order, page]);

  return { search, setSearch, sort, order, toggleSort, page, setPage, pageSize: PAGE_SIZE, params };
}
